import {
  Button as AquiloButton,
  IconActionButton,
  ToolTip,
  InboxFilterBar,
  SimpleCheckboxControlled,
} from "@truevault/aquilo";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "react-table";
import {
  ArchiveBoxArrowDownIcon,
  ArrowUturnLeftIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import { useOrganization, usePrimaryOrganizationId } from "../../hooks";
import { RequestInboxTabs } from "./RequestListingPage";
import {
  usePrivacyEmailBulkAction,
  usePrivacyEmailConversations,
} from "../../hooks/usePrivacyEmails";
import useStatefulPagination from "../../../common/hooks/useStatefulPagination";
import { RouteHelpers, Routes } from "../../root/routes";
import {
  useRowSelectionManager,
  UseRowSelectionManager,
} from "../../../common/hooks/useRowSelectionManager";
import { fmtDate } from "../../formatters/date";
import { PostmarkOrganizationConversationSummaryDto } from "../../../common/service/server/dto/PostmarkInboxDto";
import { canUsePrivacyEmailInbox } from "../../util/privacyEmailSetup";
import { PaginatedTable } from "../../../common/components/table/PaginatedTable";
import {
  NewRequestDialog,
  NewRequestDialogPrefill,
} from "../../components/dialog/NewRequestDialog";
import { useRequestSummaries } from "../../hooks/useRequests";

const PAGE_SIZE = 25;
const STATUS_FILTER_OPTIONS = [
  { label: "Inbox", value: "INBOX" },
  { label: "Archive", value: "ARCHIVED" },
  { label: "All mail", value: "ALL" },
];

type ConversationBulkHeaderArgs = {
  rowSelectionManager: UseRowSelectionManager<PostmarkOrganizationConversationSummaryDto>;
  selectedRows: PostmarkOrganizationConversationSummaryDto[];
  allDataForCurrentFilter?: PostmarkOrganizationConversationSummaryDto[];
  clearSelection: () => void;
  selectAllVisible: () => void;
};

const parseParticipantName = (
  displayName: string | undefined,
): { firstName?: string; lastName?: string } => {
  if (!displayName) {
    return {};
  }

  const trimmed = displayName.trim();
  if (!trimmed) {
    return {};
  }

  if (trimmed.includes(",")) {
    const [lastSegment, rest] = trimmed.split(",", 2);
    const restParts = rest?.trim().split(/\s+/).filter(Boolean) ?? [];
    const firstName = restParts.shift();
    const lastNameSegments = [lastSegment.trim(), ...restParts].filter(Boolean);

    return {
      firstName,
      lastName: lastNameSegments.length > 0 ? lastNameSegments.join(" ") : undefined,
    };
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0] };
  }

  const firstName = parts.shift();
  const lastName = parts.length ? parts.join(" ") : undefined;
  return { firstName, lastName };
};

export const PrivacyEmailsPage = (_props: RouteComponentProps) => {
  const orgId = usePrimaryOrganizationId();
  const history = useHistory();
  const location = useLocation();
  const [org, orgRequest] = useOrganization(orgId, true);
  const showPrivacyEmails = canUsePrivacyEmailInbox(org);

  useEffect(() => {
    if (org && !showPrivacyEmails) {
      history.replace(Routes.requests.active);
    }
  }, [org, showPrivacyEmails, history]);

  const {
    state: paginationState,
    updatePage,
    updateSearch,
    updateFilter,
  } = useStatefulPagination({
    filter: [{ id: "status", value: "INBOX" }],
  });

  const statusFilter = useMemo<string | undefined>(() => {
    const filterEntry = (paginationState.filter ?? []).find((filter) => filter.id === "status");
    if (filterEntry && typeof filterEntry.value === "string") {
      return filterEntry.value;
    }
    return undefined;
  }, [paginationState.filter]);

  const selectedStatusOption = statusFilter ?? "INBOX";
  const currentPage = paginationState.page ?? 1;
  const queryParams = useMemo(() => {
    const params: {
      page: number;
      per: number;
      q?: string;
      status?: string;
    } = {
      page: Math.max(currentPage - 1, 0),
      per: PAGE_SIZE,
      q: paginationState.search,
    };

    params.status = selectedStatusOption;

    return params;
  }, [currentPage, paginationState.search, selectedStatusOption]);

  const {
    result: conversationsPage,
    request,
    refresh,
  } = usePrivacyEmailConversations(orgId, queryParams, showPrivacyEmails);

  const conversations = useMemo(
    () =>
      (conversationsPage?.data ?? []).map((conversation) => ({
        ...conversation,
        participantsPreview: Array.isArray(conversation.participantsPreview)
          ? conversation.participantsPreview
          : [],
        messageCount: typeof conversation.messageCount === "number" ? conversation.messageCount : 0,
      })),
    [conversationsPage],
  );
  const meta = conversationsPage?.meta;

  const rowSelectionManager = useRowSelectionManager<PostmarkOrganizationConversationSummaryDto>({
    getVisibleRows: () => conversations.map((row) => ({ locator: row.id, item: row })),
  });
  const selectedItems = useMemo(
    () => Object.values(rowSelectionManager.selectedRowsHash),
    [rowSelectionManager.selectedRowsHash],
  );
  const hasSelection = selectedItems.length > 0;
  const [newRequestPrefill, setNewRequestPrefill] = useState<NewRequestDialogPrefill | undefined>();
  const [newRequestConversationId, setNewRequestConversationId] = useState<string | null>(null);
  const [newRequestDialogOpen, setNewRequestDialogOpen] = useState(false);
  const searchValue = paginationState.search ?? "";

  const activeStatusChips = useMemo(() => {
    if (selectedStatusOption === "ALL") return [];
    const chipLabel =
      STATUS_FILTER_OPTIONS.find((option) => option.value === selectedStatusOption)?.label ??
      selectedStatusOption;
    return [
      {
        label: chipLabel,
        value: selectedStatusOption,
      },
    ];
  }, [selectedStatusOption]);

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      const existingFilters = paginationState.filter ?? [];
      const filtersWithoutStatus = existingFilters.filter((filter) => filter.id !== "status");
      updateFilter([...filtersWithoutStatus, { id: "status", value }]);
    },
    [paginationState.filter, updateFilter],
  );

  const bulkAction = usePrivacyEmailBulkAction(orgId);
  const [openRequestsSummary] = useRequestSummaries(
    orgId,
    "active",
    undefined,
    undefined,
    undefined,
    "all",
    0,
    1,
  );
  const openRequestsCount = openRequestsSummary?.meta?.totalCount;

  const handleOpenCreateRequest = useCallback(
    (conversation: PostmarkOrganizationConversationSummaryDto) => {
      const customerParticipant = conversation.participantsPreview?.find(
        (participant) => participant.direction === "CUSTOMER",
      );

      const { firstName, lastName } = parseParticipantName(customerParticipant?.displayName);

      setNewRequestPrefill({
        subjectFirstName: firstName,
        subjectLastName: lastName,
        subjectEmailAddress: customerParticipant?.emailAddress,
        sendEmailVerification: false,
      });
      setNewRequestConversationId(conversation.id);
      setNewRequestDialogOpen(true);
    },
    [],
  );

  const columns = useMemo<Column<PostmarkOrganizationConversationSummaryDto>[]>(() => {
    const returnToInboxUrl = `${RouteHelpers.requests.privacyEmails()}${location.search || ""}`;

    return buildConversationColumns(rowSelectionManager, refresh, bulkAction, {
      onOpenConversation: (conversationId) =>
        history.push(RouteHelpers.requests.privacyEmailConversation(conversationId), {
          returnToInboxUrl,
        }),
      onCreateRequest: handleOpenCreateRequest,
      onGoToRequest: (requestId) => history.push(RouteHelpers.requests.detail(requestId)),
    });
  }, [rowSelectionManager, refresh, bulkAction, history, handleOpenCreateRequest, location.search]);

  const handleBulk = useCallback(
    async (action: "ARCHIVE" | "UNARCHIVE" | "MARK_READ" | "MARK_UNREAD") => {
      if (!hasSelection) return;
      const ids = Array.from(
        new Set(selectedItems.flatMap((item) => item.threadConversationIds ?? [item.id])),
      );
      if (ids.length === 0) return;
      await bulkAction.fetch({ ids, action });
      rowSelectionManager.clearSelectedRows();
      await refresh();
    },
    [hasSelection, selectedItems, bulkAction, rowSelectionManager, refresh],
  );

  const renderBulkSelectionHeader = useCallback(
    ({
      clearSelection,
      selectAllVisible,
      rowSelectionManager: manager,
      selectedRows,
    }: ConversationBulkHeaderArgs) => {
      if (!manager?.hasVisibleRows) {
        return null;
      }

      const selectedCount = selectedRows.length;

      const effectiveStatus =
        statusFilter === "ARCHIVED" ? "ARCHIVED" : statusFilter === "ALL" ? "ALL" : "INBOX";

      return (
        <div className="tw-boundary">
          <div className="flex justify-between items-center pl-[8px] pr-[8px] pb-[8px] border-b border-neutral-300">
            <div className="flex items-center gap-[12px]">
              <SimpleCheckboxControlled
                name="privacy-email-select-visible"
                checked={manager.areAllVisibleRowsSelected()}
                onChange={(checked) => {
                  if (checked) {
                    selectAllVisible();
                  } else {
                    clearSelection();
                  }
                }}
              />
              <div className="text-primary-600 text-[16px] font-medium">
                {selectedCount === 1
                  ? "1 conversation selected"
                  : `${selectedCount} conversations selected`}
              </div>
            </div>
            <div className="flex items-center gap-[12px]">
              <AquiloButton size="s" color="tertiary" onClick={clearSelection}>
                Clear Selection
              </AquiloButton>
              <AquiloButton size="s" color="tertiary" onClick={selectAllVisible}>
                Select visible
              </AquiloButton>
            </div>
            <div className="flex items-center gap-[12px]">
              <AquiloButton
                size="s"
                color="tertiary"
                onClick={() => {
                  void (effectiveStatus === "ARCHIVED"
                    ? handleBulk("UNARCHIVE")
                    : handleBulk("ARCHIVE"));
                }}
              >
                {effectiveStatus === "ARCHIVED" ? (
                  <>
                    <ArchiveBoxArrowDownIcon className="w-[14px] h-[14px] mr-[8px] rotate-180" />
                    Unarchive
                  </>
                ) : (
                  <>
                    <ArchiveBoxArrowDownIcon className="w-[14px] h-[14px] mr-[8px]" />
                    Archive
                  </>
                )}
              </AquiloButton>
              <AquiloButton
                size="s"
                color="tertiary"
                onClick={() => {
                  void handleBulk("MARK_READ");
                }}
              >
                <EnvelopeOpenIcon className="w-[14px] h-[14px] mr-[8px]" />
                Mark as read
              </AquiloButton>
              <AquiloButton
                size="s"
                color="tertiary"
                onClick={() => {
                  void handleBulk("MARK_UNREAD");
                }}
              >
                <EnvelopeIcon className="w-[14px] h-[14px] mr-[8px]" />
                Mark unread
              </AquiloButton>
            </div>
          </div>
        </div>
      );
    },
    [handleBulk, statusFilter],
  );

  if (!org && orgRequest.running) {
    return <LoadingPageWrapper />;
  }

  if (org && !showPrivacyEmails) {
    return <LoadingPageWrapper />;
  }

  if (!conversationsPage && request.running) {
    return <LoadingPageWrapper />;
  }

  const newRequestButton = (
    <AquiloButton
      size="s"
      color="primary"
      onClick={() => {
        setNewRequestConversationId(null);
        setNewRequestPrefill(undefined);
        setNewRequestDialogOpen(true);
      }}
      disabled={hasSelection}
    >
      <span className="inline-flex items-center whitespace-nowrap">
        <PlusIcon className="w-[14px] h-[14px] mr-[8px]" /> New Request
      </span>
    </AquiloButton>
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Privacy Emails" />

      <div style={{ maxWidth: "1214px", paddingRight: "32px" }}>
        <div className="tw-boundary">
          <div className="flex justify-between items-end mb-6">
            <RequestInboxTabs
              selectedTabKey="privacy-emails"
              counts={{ open: openRequestsCount, privacyEmails: meta?.totalCount }}
            />
            <div className="ml-8 shrink-0">
              {hasSelection ? (
                <ToolTip text="Clear the current selection before creating a new request.">
                  <div>{newRequestButton}</div>
                </ToolTip>
              ) : (
                newRequestButton
              )}
            </div>
          </div>

          <div>
            <InboxFilterBar
              searchValue={searchValue}
              onSearchChange={updateSearch}
              onSearchClear={() => updateSearch("")}
              filterOptions={STATUS_FILTER_OPTIONS}
              selectedFilterValue={selectedStatusOption}
              onFilterSelect={handleStatusFilterChange}
              activeFilters={activeStatusChips}
              onRemoveFilter={() => handleStatusFilterChange("ALL")}
            />
          </div>

          <PaginatedTable<PostmarkOrganizationConversationSummaryDto>
            data={conversations}
            columns={columns}
            pageMeta={meta}
            rowSelectionManager={rowSelectionManager}
            allDataForCurrentFilter={conversations}
            initialPageIndex={Math.max(currentPage - 1, 0)}
            onPageChange={updatePage}
            filterPanelOpen={false}
            showFilters={false}
            enableRowClick={() => true}
            onRowClick={(row) => {
              const conversation = row.original as PostmarkOrganizationConversationSummaryDto;
              history.push(RouteHelpers.requests.privacyEmailConversation(conversation.id), {
                returnToInboxUrl: `${RouteHelpers.requests.privacyEmails()}${
                  location.search || ""
                }`,
              });
            }}
            emptyState={
              <p className="py-lg text-center text-sm text-neutral-500">No conversations yet.</p>
            }
            kind="basic"
            renderBulkSelectionHeader={renderBulkSelectionHeader}
            showColumnHeaders={false}
            visuallyHiddenColumnIds={["status"]}
          />
        </div>
      </div>

      {newRequestDialogOpen && (
        <NewRequestDialog
          key={newRequestConversationId ?? "new-request"}
          open={newRequestDialogOpen}
          prefill={newRequestPrefill}
          postmarkConversationId={newRequestConversationId ?? undefined}
          onPostmarkSuccess={async () => {
            await refresh();
          }}
          onClose={() => {
            setNewRequestDialogOpen(false);
            setNewRequestPrefill(undefined);
            setNewRequestConversationId(null);
          }}
        />
      )}
    </StandardPageWrapper>
  );
};

type ConversationColumnHandlers = {
  onOpenConversation: (conversationId: string) => void;
  onCreateRequest: (conversation: PostmarkOrganizationConversationSummaryDto) => void;
  onGoToRequest: (requestPublicId: string) => void;
};

function buildConversationColumns(
  rowSelectionManager: UseRowSelectionManager<PostmarkOrganizationConversationSummaryDto>,
  refresh: () => Promise<void>,
  bulkAction: ReturnType<typeof usePrivacyEmailBulkAction>,
  handlers: ConversationColumnHandlers,
): Column<PostmarkOrganizationConversationSummaryDto>[] {
  return [
    {
      id: "select",
      Header: () => <SelectionHeader rowSelectionManager={rowSelectionManager} />,
      Cell: ({ row }) => (
        <SelectionCell conversation={row.original} rowSelectionManager={rowSelectionManager} />
      ),
      disableSortBy: true,
      disableFilters: true,
      width: 61,
    },
    {
      Header: "From",
      id: "from",
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }) => <FromCell conversation={row.original} />,
      width: 200,
    },
    {
      Header: "Subject",
      accessor: "subject",
      disableSortBy: true,
      disableFilters: true,
      width: 701,
      Cell: ({ row }) => <SubjectCell conversation={row.original} />,
    },
    {
      Header: "Status",
      accessor: "status",
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }) => (
        <div className="tw-boundary text-[14px] font-medium text-neutral-500">
          {row.original.status === "ARCHIVED" ? "Archived" : "Open"}
        </div>
      ),
      width: 140,
    },
    {
      Header: () => <div className="tw-boundary text-right pr-[8px]">Last activity</div>,
      id: "lastActivity",
      disableSortBy: true,
      disableFilters: true,
      width: 196,
      Cell: ({ row }) => (
        <LastActivityCell
          conversation={row.original}
          refresh={refresh}
          bulkAction={bulkAction}
          onOpenConversation={handlers.onOpenConversation}
          onCreateRequest={handlers.onCreateRequest}
          onGoToRequest={handlers.onGoToRequest}
        />
      ),
    },
  ];
}

type SelectionHeaderProps = {
  rowSelectionManager: UseRowSelectionManager<PostmarkOrganizationConversationSummaryDto>;
};

const SelectionHeader = ({ rowSelectionManager }: SelectionHeaderProps) => (
  <div className="tw-boundary">
    <SimpleCheckboxControlled
      name="privacy-email-select-all"
      checked={rowSelectionManager.areAllVisibleRowsSelected()}
      onChange={(checked) => rowSelectionManager.setSelectionForVisible(checked)}
    />
  </div>
);

type SelectionCellProps = {
  conversation: PostmarkOrganizationConversationSummaryDto;
  rowSelectionManager: UseRowSelectionManager<PostmarkOrganizationConversationSummaryDto>;
};

const SelectionCell = ({ conversation, rowSelectionManager }: SelectionCellProps) => (
  <div className="tw-boundary">
    <SimpleCheckboxControlled
      name={`privacy-email-${conversation.id}`}
      checked={rowSelectionManager.isRowSelected(conversation.id)}
      onChange={(checked) =>
        rowSelectionManager.onRowSelectChange(conversation.id, checked, conversation)
      }
    />
  </div>
);

type SubjectCellProps = {
  conversation: PostmarkOrganizationConversationSummaryDto;
};

const SubjectCell = ({ conversation }: SubjectCellProps) => {
  const previewSnippet = (conversation.preview ?? "").trim();

  return (
    <div className="tw-boundary w-[701px] max-w-[701px] min-w-0 overflow-hidden">
      <div className="flex w-full min-w-0 items-center gap-[12px] text-[18px] leading-[28px]">
        <span className="max-w-[240px] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap font-normal text-neutral-900">
          {conversation.subject || "(No subject)"}
        </span>
        <span className="min-w-0 grow overflow-hidden text-ellipsis whitespace-nowrap font-normal text-neutral-600">
          {previewSnippet}
        </span>
        {conversation.hasAttachments && (
          <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-neutral-400" aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

type FromCellProps = {
  conversation: PostmarkOrganizationConversationSummaryDto;
};

const FromCell = ({ conversation }: FromCellProps) => {
  const previews = Array.isArray(conversation.participantsPreview)
    ? conversation.participantsPreview
    : [];

  const labels = previews
    .map((participant) =>
      participant.direction === "INTERNAL"
        ? "me"
        : participant.displayName?.trim() || participant.emailAddress,
    )
    .filter(Boolean);

  const uniqueLabels = Array.from(new Set(labels));
  const participantsLabel = uniqueLabels.length > 0 ? uniqueLabels.join(", ") : "(No participants)";
  const messageCount =
    typeof conversation.messageCount === "number" ? conversation.messageCount : 0;
  const labelWithCount = `${participantsLabel} ${messageCount}`;

  return (
    <div className="tw-boundary">
      <div className="w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-[18px] leading-[28px] text-neutral-900 font-medium">
        {labelWithCount}
      </div>
    </div>
  );
};

type LastActivityCellProps = {
  conversation: PostmarkOrganizationConversationSummaryDto;
  refresh: () => Promise<void>;
  bulkAction: ReturnType<typeof usePrivacyEmailBulkAction>;
  onOpenConversation: (conversationId: string) => void;
  onCreateRequest: (conversation: PostmarkOrganizationConversationSummaryDto) => void;
  onGoToRequest: (requestPublicId: string) => void;
};

const LastActivityCell = ({
  conversation,
  refresh,
  bulkAction,
  onOpenConversation,
  onCreateRequest,
  onGoToRequest,
}: LastActivityCellProps) => {
  const [showActions, setShowActions] = React.useState(false);

  const threadIds = React.useMemo(() => {
    const ids = conversation.threadConversationIds ?? [];
    const combined = [conversation.id, ...ids];
    return Array.from(new Set(combined.filter(Boolean)));
  }, [conversation.id, conversation.threadConversationIds]);

  const handleArchive = async () => {
    if (threadIds.length === 0) return;
    const action: "ARCHIVE" | "UNARCHIVE" =
      conversation.status === "ARCHIVED" ? "UNARCHIVE" : "ARCHIVE";
    await bulkAction.fetch({ ids: threadIds, action });
    await refresh();
  };

  const handleToggleUnread = async () => {
    if (threadIds.length === 0) return;
    const action: "MARK_READ" | "MARK_UNREAD" =
      conversation.unreadCount > 0 ? "MARK_READ" : "MARK_UNREAD";
    await bulkAction.fetch({ ids: threadIds, action });
    await refresh();
  };

  const formattedDate = fmtDate(conversation.lastActivityAt, "MMM D, h:mm a");

  const handleBlurCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    const currentTarget = event.currentTarget;
    requestAnimationFrame(() => {
      if (!currentTarget.contains(document.activeElement)) {
        setShowActions(false);
      }
    });
  };

  return (
    <div
      className="last-activity-cell min-w-[196px]"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onFocusCapture={() => setShowActions(true)}
      onBlurCapture={handleBlurCapture}
    >
      <div
        className={clsx(
          "last-activity-cell__timestamp whitespace-nowrap",
          showActions && "last-activity-cell__timestamp--hidden",
        )}
      >
        {formattedDate}
      </div>
      <div
        className={clsx(
          "last-activity-cell__actions tw-boundary",
          showActions && "last-activity-cell__actions--visible",
        )}
      >
        <ToolTip text={conversation.status === "ARCHIVED" ? "Unarchive" : "Archive"}>
          <IconActionButton
            onClick={(event) => {
              event.stopPropagation();
              void handleArchive();
            }}
            disabled={bulkAction.request.running}
          >
            <ArchiveBoxArrowDownIcon
              className={clsx(
                "text-neutral-500",
                conversation.status === "ARCHIVED" && "transform rotate-180",
              )}
            />
          </IconActionButton>
        </ToolTip>
        <ToolTip text="Reply">
          <IconActionButton
            onClick={(event) => {
              event.stopPropagation();
              onOpenConversation(conversation.id);
            }}
            disabled={bulkAction.request.running}
          >
            <ArrowUturnLeftIcon className="text-neutral-500" />
          </IconActionButton>
        </ToolTip>
        <ToolTip text={conversation.unreadCount > 0 ? "Mark as read" : "Mark as unread"}>
          <IconActionButton
            onClick={(event) => {
              event.stopPropagation();
              void handleToggleUnread();
            }}
            disabled={bulkAction.request.running}
          >
            {conversation.unreadCount > 0 ? (
              <EnvelopeOpenIcon className="text-neutral-500" />
            ) : (
              <EnvelopeIcon className="text-neutral-500" />
            )}
          </IconActionButton>
        </ToolTip>
        {conversation.primaryRequestPublicId ? (
          <ToolTip text="Go to request">
            <IconActionButton
              className="bg-primary-50 text-primary-700 hover:bg-primary-100"
              onClick={(event) => {
                event.stopPropagation();
                onGoToRequest(conversation.primaryRequestPublicId!);
              }}
              disabled={bulkAction.request.running}
            >
              <DocumentTextIcon />
            </IconActionButton>
          </ToolTip>
        ) : (
          <ToolTip text="Make DSAR">
            <IconActionButton
              onClick={(event) => {
                event.stopPropagation();
                onCreateRequest(conversation);
              }}
              disabled={bulkAction.request.running}
            >
              <PlusCircleIcon className="text-neutral-500" />
            </IconActionButton>
          </ToolTip>
        )}
      </div>
    </div>
  );
};

export default PrivacyEmailsPage;
